/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Spin,
  DatePicker,
  Modal,
} from 'antd';
import type { DatePickerProps } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { type RootState, type AppDispatch } from '../../redux/store';
import {
  type Metal,
  type SaleItem,
  type CreateSaleRequest,
} from './store/salesTypes';
import ItemDescription from '../Loan/ItemDescription';
import { createNewSaleAction, getSaleTypesAction } from './store/salesActions';
import PrintableContent from '../Loan/PrintableContent';
import ImageUpload from '../../components/ImageUpload';
import WebcamImage from '../../components/ImageUpload/webCamUpload';
import { getCustomerByIdAction } from '../Customer/store/customerActions';
import {
  documentChargeWaiverPercent,
  expectedDocumentCharge,
  requiredApproval,
  useBranchApprovalWorkflow,
} from '../Settings/useBranchApprovalWorkflow';

const { TextArea } = Input;

interface SelectOption {
  label: string;
  value: string;
}

export default function SalesIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [principal, setPrincipal] = useState(0);
  const [offlinePrincipal, setOfflinePrincipal] = useState(0);
  const [onlinePrincipal, setOnlinePrincipal] = useState(0);
  const [amountToGiveCustomer, setAmountToGiveCustomer] = useState(0);
  const [approvalType, setApprovalType] = useState('');
  const [additionalAmount, setAdditionalAmount] = useState(0);
  const [invalidOfflineAmount, setInvalidOfflineAmount] = useState(false);
  const [invalidOnlineAmount, setInvalidOnlineAmount] = useState(false);

  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [defaultMetalValue, setDefaultMetalValue] = useState('');

  form.setFieldsValue({
    customer: searchParams.get('customerId'),
  });
  form.setFieldsValue({ startDate: dayjs() });

  const getSaleTypes = async (): Promise<void> => {
    await getSaleTypesAction(dispatch);
    if (customerId !== '') {
      await getCustomerByIdAction(dispatch, { customerId });
    }
  };

  useEffect(() => {
    void getSaleTypes();
  }, []);

  const { metal }: any = useSelector<any>((state) => state.sales, shallowEqual);
  const { customer }: any = useSelector<RootState>(
    (rootState) => rootState.customer
  );
  useEffect(() => {
    if (metal.length > 0) {
      const metalList: Array<{ label: string; value: string }> = [];
      for (const x of metal) {
        if (x.price > 0) {
          metalList.push({
            label: x.name,
            value: x.name,
          });
        }
      }
      setMetalOptions(metalList);
      if (metalList.length > 0) {
        form.setFieldsValue({ metal: metalList[0].value });
        setDefaultMetalValue(metalList[0].value);
        handleChangeMetal(metalList[0].value);
      }
    }
  }, [metal]);

  const [branchOptions, setBranchOptions] = useState<
    Array<{
      label: string;
      value: string;
    }>
  >([]);

  const [branch, setBranch] = useState<string>('');
  const approvalWorkflow = useBranchApprovalWorkflow(branch);
  const saleDocumentCharge = (amount: number): number =>
    parseFloat(
      expectedDocumentCharge(
        amount,
        approvalWorkflow?.documentChargeRates.sale
      ).toFixed(2)
    );
  const additionalAmountApproval = requiredApproval(
    approvalWorkflow?.rules.saleAdditionalAmount,
    Number(additionalAmount)
  );

  const onFinish = async (values: CreateSaleRequest) => {
    setIsLoading(true);
    const response = await createNewSaleAction(dispatch, {
      ...values,
      branch,
      jewels: modifyItemsToJewels(items),
      photo,
      startDate: dayjs(datePickerDate).toISOString(),
      principal: Number(principal),
      calculatedPrincipal: Number(amountToGiveCustomer),
      onlinePrincipal: Number(onlinePrincipal),
      offlinePrincipal: Number(offlinePrincipal),
      additionalAmount: Number(additionalAmount),
    });
    setIsLoading(false);

    if (response?.saleNumber) {
      setPrintSaleDetails(response);
      setIsPrintModalOpen(true);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  const [showWebCam, setShowWebCam] = useState(false);
  const handleWebCam = (): void => {
    setShowWebCam(!showWebCam);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printSaleDetails, setPrintSaleDetails] = useState<any>();
  const [metalOptions, setMetalOptions] = useState<SelectOption[]>([]);
  const [purityOptions, setPurityOptions] = useState<SelectOption[]>([]);
  const [metalType, setMetalType] = useState<SelectOption[]>([]);

  const [customerId, setCustomerId] = useState<string>(
    searchParams.get('customerId') ?? ''
  );
  const [documentCharges, setDocumentCharges] = useState<number>();
  const [items, setItems] = useState<SaleItem[]>([]);
  const [photo, setPhoto] = useState<string>('');

  useEffect(() => {
    const branches = localStorage.getItem('branches');
    if (branches != null) {
      const parsedBranches = JSON.parse(branches);
      const branchOptionTemp: Array<{ label: string; value: string }> =
        parsedBranches.map((branch: { code: string; id: string }) => ({
          label: branch.code,
          value: branch.id,
        }));
      setBranchOptions(branchOptionTemp);

      // Auto-populate branch from customerId if present
      if (customerId !== '') {
        const branchCode = customerId.substring(0, 3);
        const matchingBranch = parsedBranches.find(
          (b: { code: string; id: string }) => b.code === branchCode
        );
        if (matchingBranch != null) {
          setBranch(matchingBranch.id);
          form.setFieldsValue({
            branch: matchingBranch.id,
          });
        } else {
          setBranch(parsedBranches[0].id);
          form.setFieldsValue({
            branch: parsedBranches[0].id,
          });
        }
      } else {
        setBranch(branchOptionTemp[0]?.value ?? '');
        form.setFieldsValue({
          branch: branchOptionTemp[0]?.value,
        });
      }
    }
  }, [customerId]);

  const handleChangeMetal = (value: string): void => {
    const metalInfo: Metal = metal.filter((x: Metal) => x.name === value)[0];
    setDefaultMetalValue(value);
    form.setFieldsValue({ metal: value });

    const purityOptionsMap: SelectOption[] = metalInfo?.purityQuality.map(
      (x) => {
        return {
          label: String(`${x.purityName}`),
          value: String(x.purityPercentage),
        };
      }
    );
    setPurityOptions(purityOptionsMap);

    const metalTypesMap: SelectOption[] = metalInfo?.types.map((x) => {
      return {
        label: x,
        value: x,
      };
    });
    setMetalType(metalTypesMap);
  };

  const modifyItemsToJewels = (items: SaleItem[]) => {
    return items.map((item) => {
      return {
        itemType: item.itemName ?? '',
        purity:
          purityOptions.filter(
            (x) => Number(x.value) === Number(item.purity)
          )[0]?.label ?? '',
        grossWeight: parseFloat(Number(item.grossWt).toFixed(2)),
        unwantedWeight: parseFloat(Number(item.stoneWt).toFixed(2)),
        netWeight: parseFloat(Number(item.netWt).toFixed(2)),
        quantity: item.quantity,
        _id: item._id,
        // salePrice: item.finalAmt,
      };
    });
  };

  const [datePickerDate, setDatePickerDate] = useState<Dayjs | null>(dayjs());

  const handleUpdateItems = (updatedItems: SaleItem[]) => {
    let maxPrice = 0;
    const processedItems = updatedItems.map((x: SaleItem) => {
      const updatedItem = { ...x };
      updatedItem.netWt = String(
        Number(updatedItem.grossWt) - Number(updatedItem.stoneWt)
      );
      const selectedMetal: Metal = metal.filter(
        (m: Metal) => m.name === form.getFieldValue('metal')
      )[0];
      console.log('selectedMetal', selectedMetal);
      updatedItem.finalAmt =
        Number(selectedMetal.salePrice) *
        (Number(updatedItem.purity ?? 0) / 100);
      updatedItem.finalAmt = Number(updatedItem.finalAmt) * (100 / 100);
      updatedItem.finalAmt =
        Number(updatedItem.netWt ?? 0) * updatedItem.finalAmt;
      updatedItem.finalAmt = Math.floor(updatedItem.finalAmt);
      maxPrice = Number(maxPrice) + Number(updatedItem.finalAmt);
      return updatedItem;
    });

    const finalAmount = Math.floor(maxPrice);
    setAmountToGiveCustomer(finalAmount);

    setItems(processedItems);
  };

  const onChangeDate: DatePickerProps['onChange'] = (
    date: Dayjs | null,
    dateString: string
  ): void => {
    form.setFieldsValue({ startDate: dateString });
    setDatePickerDate(date);
  };

  const handlePrintClose = () => {
    setIsPrintModalOpen(false);
    navigate(`/customer?customerId=${customerId}&tab=2`);
  };

  return (
    <Form
      form={form}
      name="sale_form"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
    >
      <Spin spinning={isLoading} fullscreen />
      <div className="loan__index__page">
        <div className="loan__form__input">
          <Form.Item
            name="customer"
            rules={[{ required: true, message: 'CustomerID required!' }]}
          >
            <div className="loan__input__container">
              <p className="loan__input__label">Customer ID</p>
              <Input
                disabled={Boolean(searchParams.get('customerId'))}
                value={customerId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  form.setFieldsValue({ customer: e?.target?.value });
                  setCustomerId(e?.target?.value);
                }}
                className="loan__input"
                placeholder="Enter Customer ID"
              />
            </div>
          </Form.Item>

          <Form.Item
            name="branch"
            rules={[{ required: true, message: 'Please select branch!' }]}
          >
            <div className="loan__input__container">
              <p className="loan__input__label">Branch</p>
              <Select
                style={{ width: 311 }}
                placeholder="Select branch"
                value={branch}
                onChange={(value: string): void => {
                  form.setFieldsValue({ branch: value });
                  setBranch(value);
                }}
                options={branchOptions}
                className="loan__select"
              />
            </div>
          </Form.Item>
          <Form.Item
            name="metal"
            rules={[{ required: true, message: 'Please select metal!' }]}
          >
            <div className="loan__input__container">
              <p className="loan__input__label">Metal</p>
              <Select
                value={defaultMetalValue}
                style={{ width: 311 }}
                placeholder="Select metal"
                onChange={(value: string): void => {
                  handleChangeMetal(value);
                }}
                options={metalOptions}
                className="loan__select"
              />
            </div>
          </Form.Item>
        </div>

        <div className="loan__items__section">
          <p className="loan__items__title">Item Description</p>
          <ItemDescription
            items={items}
            canEditNetWeight={false}
            purityDetails={purityOptions}
            metalType={metalType}
            updateItems={(updatedItems: any) => {
              handleUpdateItems(updatedItems);
            }}
          />
        </div>

        <div className="loan__form__input">
          <div className="loan__input__container">
            <p className="loan__input__label">Amount to Give Customer</p>
            <InputNumber
              type="number"
              value={amountToGiveCustomer}
              style={{ width: 311 }}
              disabled
              controls={false}
              className="loan__input"
            />
          </div>

          <Form.Item
            name="principal"
            rules={[{ required: true, message: 'Please enter principal!' }]}
          >
            <div className="loan__input__container">
              <p className="loan__input__label">Principal</p>
              <Input
                type="number"
                value={principal}
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  form.setFieldsValue({ principal: e.target.value });
                  setPrincipal(Number(e.target.value));

                  const totalPrincipal =
                    Number(e.target.value) + Number(additionalAmount);
                  const calcDocCharges = saleDocumentCharge(totalPrincipal);
                  setDocumentCharges(calcDocCharges);
                  form.setFieldsValue({ documentCharge: calcDocCharges });
                  setApprovalType('');

                  if (
                    Number(e.target.value) + Number(additionalAmount) !==
                    Number(onlinePrincipal) + Number(offlinePrincipal)
                  ) {
                    setInvalidOfflineAmount(true);
                    setInvalidOnlineAmount(true);
                  } else {
                    setInvalidOfflineAmount(false);
                    setInvalidOnlineAmount(false);
                  }
                }}
                className="loan__input"
                placeholder="Enter principal"
              />
            </div>
          </Form.Item>

          <Form.Item
            name="onlinePrincipal"
            rules={[
              { required: true, message: 'Please enter online principal!' },
            ]}
          >
            <div className="loan__input__container">
              <p className="loan__input__label">Online Principal</p>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  form.setFieldsValue({
                    onlinePrincipal: e.target.value,
                  });
                  setOnlinePrincipal(Number(e.target.value));

                  if (
                    Number(e.target.value) + Number(offlinePrincipal) !==
                    Number(principal) + Number(additionalAmount)
                  ) {
                    setInvalidOnlineAmount(true);
                    setInvalidOfflineAmount(true);
                  } else {
                    setInvalidOnlineAmount(false);
                    setInvalidOfflineAmount(false);
                  }
                }}
                className="loan__input"
                placeholder="Enter online principal"
              />
              {invalidOnlineAmount && (
                <p className="error__message">Invalid online principal</p>
              )}
            </div>
          </Form.Item>

          <Form.Item
            name="offlinePrincipal"
            rules={[
              { required: true, message: 'Please enter offline principal!' },
            ]}
          >
            <div className="loan__input__container">
              <p className="loan__input__label">Offline Principal</p>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  form.setFieldsValue({
                    offlinePrincipal: e.target.value,
                  });
                  setOfflinePrincipal(Number(e.target.value));

                  if (
                    Number(e.target.value) + Number(onlinePrincipal) !==
                    Number(principal) + Number(additionalAmount)
                  ) {
                    setInvalidOfflineAmount(true);
                    setInvalidOnlineAmount(true);
                  } else {
                    setInvalidOfflineAmount(false);
                    setInvalidOnlineAmount(false);
                  }
                }}
                className="loan__input"
                placeholder="Enter offline principal"
              />
              {invalidOfflineAmount && (
                <p className="error__message">Invalid offline principal</p>
              )}
            </div>
          </Form.Item>

          <Form.Item
            name="documentCharge"
            rules={[
              { required: true, message: 'Please enter document charge!' },
            ]}
          >
            <div className="loan__input__container">
              <p className="loan__input__label">Document Charge</p>
              <Input
                value={documentCharges}
                type="number"
                step="0.01"
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  form.setFieldsValue({
                    documentCharge: e.target.value,
                  });
                  setDocumentCharges(Number(e.target.value));

                  // the server checks the charge against the principal without the additional amount
                  setApprovalType(
                    requiredApproval(
                      approvalWorkflow?.rules.saleDocumentCharge,
                      documentChargeWaiverPercent(
                        expectedDocumentCharge(
                          principal,
                          approvalWorkflow?.documentChargeRates.sale
                        ),
                        Number(e.target.value)
                      )
                    )
                  );
                }}
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                className="loan__input"
                placeholder="Enter document charge"
              />
              {approvalType === 'admin' && (
                <p
                  style={{
                    color: '#DD1E2D',
                    marginTop: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Needs admin approval
                </p>
              )}
              {approvalType === 'manager' && (
                <p
                  style={{
                    color: '#DD1E2D',
                    marginTop: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Needs manager approval
                </p>
              )}
            </div>
          </Form.Item>

          <Form.Item name="additionalAmount">
            <div className="loan__input__container">
              <p className="loan__input__label">Additional amount</p>
              <Input
                value={additionalAmount}
                type="number"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  form.setFieldsValue({
                    additionalAmount: e.target.value,
                  });
                  setAdditionalAmount(Number(e.target.value));

                  const totalPrincipal =
                    Number(principal) + Number(e.target.value);
                  const calculatedDoc = saleDocumentCharge(totalPrincipal);
                  setDocumentCharges(calculatedDoc);
                  form.setFieldsValue({ documentCharge: calculatedDoc });
                  setApprovalType('');

                  // Validate online/offline equality with principal + additionalAmount
                  if (
                    Number(principal) + Number(e.target.value) !==
                    Number(onlinePrincipal) + Number(offlinePrincipal)
                  ) {
                    setInvalidOfflineAmount(true);
                    setInvalidOnlineAmount(true);
                  } else {
                    setInvalidOfflineAmount(false);
                    setInvalidOnlineAmount(false);
                  }
                }}
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                className="loan__input"
                placeholder="Enter additional amount"
              />
              {additionalAmountApproval !== 'none' && (
                <p
                  style={{
                    color: '#DD1E2D',
                    marginTop: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Needs {additionalAmountApproval} approval
                </p>
              )}
            </div>
          </Form.Item>

          <Form.Item name="startDate">
            <div className="loan__input__container">
              <p className="loan__input__label">Start Date</p>
              <DatePicker
                defaultValue={dayjs()}
                format="DD-MM-YYYY"
                onChange={onChangeDate}
                style={{ width: 311, height: 56 }}
                className="loan__select"
              />
            </div>
          </Form.Item>

          <Form.Item name="remarks">
            <div style={{ width: 311 }}>
              <p className="loan__input__label">Remarks</p>
              <TextArea
                className="loan__textarea"
                rows={4}
                placeholder="Enter remarks"
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  form.setFieldsValue({ remarks: e?.target?.value });
                }}
              />
            </div>
          </Form.Item>

          <Form.Item
            name="photo"
            rules={[{ required: true, message: 'Sale Image is required' }]}
          >
            <div className="loan__photo__container">
              <p className="loan__input__label">Image</p>
              <p className="loan__photo__sub__title">
                Image format .jpg .jpeg .png
              </p>
              <p className="loan__photo__sub__title">
                Minimum size 300px x 300px
              </p>
              <ImageUpload
                onImage={(image: string) => {
                  form.setFieldsValue({ photo: image });
                  setPhoto(image);
                  setShowWebCam(false);
                }}
                src={photo}
              />
              {!showWebCam && (
                <p className="loan__button" onClick={handleWebCam}>
                  Start Webcam
                </p>
              )}
              {showWebCam && (
                <WebcamImage
                  name="photo"
                  onChange={(e: {
                    target: { name: string; value: string };
                  }) => {
                    form.setFieldsValue({ photo: e?.target?.value });
                    setPhoto(e?.target?.value);
                  }}
                />
              )}
            </div>
          </Form.Item>
        </div>

        <div className="loan__actions__section">
          <Button
            onClick={() => {
              form.resetFields();
              setPhoto('');
            }}
            className="loan__cancel__button"
          >
            Cancel
          </Button>
          <Button
            disabled={
              isLoading ||
              invalidOfflineAmount ||
              invalidOnlineAmount ||
              principal > amountToGiveCustomer
            }
            htmlType="submit"
            className="loan__save__button"
          >
            <span>Create New Sale</span>
          </Button>
        </div>
      </div>
      <Modal
        width={1020}
        title={
          <div style={{ display: 'flex', flexDirection: 'row', gap: 24 }}>
            <span>Sale summary</span>
          </div>
        }
        open={isPrintModalOpen && printSaleDetails?.saleNumber}
        onCancel={handlePrintClose}
        footer={() => <></>}
      >
        <PrintableContent
          loanDetails={printSaleDetails}
          customer={customer}
          type="sale"
        />
      </Modal>
    </Form>
  );
}
